import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { extractDetail, extractItems, extractTotal, isRecord, readFirstText } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import {
  DEFAULT_EMBARCACOES_SORT,
  EMBARCACAO_FILTER_COLUMNS,
  formatEmbarcacoesSort,
} from "./embarcacoes.types";
import type {
  Embarcacao,
  EmbarcacaoAcomodacoes,
  EmbarcacaoAcomodacaoTipo,
  EmbarcacaoBaseTecnica,
  EmbarcacaoClasse,
  EmbarcacaoComercial,
  EmbarcacaoDimensoes,
  EmbarcacaoConsumoOperacao,
  EmbarcacaoFilterColumn,
  EmbarcacaoFilterOption,
  EmbarcacaoMotor,
  EmbarcacaoSistemasDp,
  EmbarcacaoTanques,
  EmbarcacaoVazaoBombas,
  EmbarcacoesPage,
  GetEmbarcacaoAcomodacoesParams,
  GetEmbarcacaoBaseTecnicaParams,
  GetEmbarcacaoClasseParams,
  GetEmbarcacaoComercialParams,
  GetEmbarcacaoDimensoesParams,
  GetEmbarcacaoSistemasDpParams,
  GetEmbarcacaoTanquesParams,
  GetEmbarcacaoConsumoOperacaoParams,
  GetEmbarcacaoVazaoBombasParams,
  GetEmbarcacaoMotoresParams,
  ListEmbarcacaoFilterOptionsParams,
  ListEmbarcacoesParams,
} from "./embarcacoes.types";

/** Recursos do contrato da HU 812, expostos pelo hub-bff (tarefa 1802). */
export const EMBARCACOES_PATHS = {
  LIST: "/api/embarcacoes",
} as const;

/** Opções de um filtro de coluna: um serviço por coluna filtrável. */
export function buildEmbarcacaoFilterOptionsPath(column: EmbarcacaoFilterColumn): string {
  return `${EMBARCACOES_PATHS.LIST}/filtros/${column}`;
}

/** Seção Base Técnica do detalhamento; conforme a HU 1724, apenas GET. */
export function buildEmbarcacaoBaseTecnicaPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/bases-tecnicas`;
}

/** Seção Classe do detalhamento; conforme a HU 1726, apenas GET. */
export function buildEmbarcacaoClassePath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/classes`;
}

/** Seção Comercial do detalhamento; conforme a HU 1725, apenas GET. */
export function buildEmbarcacaoComercialPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/comerciais`;
}

/** Seção Dimensões Principais do detalhamento; conforme a HU 1728, apenas GET. */
export function buildEmbarcacaoDimensoesPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/dimensoes`;
}

/** Seção Tanques do detalhamento; conforme a HU 1732, apenas GET. */
export function buildEmbarcacaoTanquesPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/tanques`;
}

/** Seção Consumo Modo Operação do detalhamento; conforme a HU 1730, apenas GET. */
export function buildEmbarcacaoConsumoOperacaoPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/consumo-operacao`;
}

/** Seção Vazão das Bombas do detalhamento; conforme a HU 1735, apenas GET. */
export function buildEmbarcacaoVazaoBombasPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/vazao-bombas`;
}

/** Seção Sistemas de DP do detalhamento; conforme a US-1799, apenas GET. */
export function buildEmbarcacaoSistemasDpPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/sistemas-dp`;
}

/** Seção Acomodações do detalhamento; conforme a US-1797, apenas GET. */
export function buildEmbarcacaoAcomodacoesPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/acomodacoes`;
}

/** Seção Motores do detalhamento; conforme a US-1800, apenas GET (lista). */
export function buildEmbarcacaoMotoresPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/motores`;
}

/** Tamanho de página do contrato, usado quando a chamada não informa o seu. */
export const DEFAULT_EMBARCACOES_PAGE_SIZE = 20;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

const COLLECTION_KEYS = ["embarcacoes", "opcoes", "filtros"];

function toEmbarcacao(raw: unknown, index: number): Embarcacao | null {
  if (!isRecord(raw)) return null;

  const acronimo = readFirstText(raw, "acronimo", "acronym", "sigla").trim();
  const nome = readFirstText(raw, "nome", "embarcacao", "name").trim();
  const tipo = readFirstText(raw, "tipo", "type").trim();
  const situacao = readFirstText(raw, "situacao", "situation", "status").trim();
  if (!acronimo && !nome && !tipo) return null;

  return {
    // O índice mantém a linha endereçável (chave da tabela) quando não vem id.
    id: readFirstText(raw, "id", "embarcacaoId").trim() || `${acronimo || nome}-${index}`,
    acronimo,
    nome,
    tipo,
    situacao,
  };
}

/** Descarta valores vazios: um filtro sem valor não restringe a consulta. */
function cleanValues(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter((value) => value !== "");
}

/**
 * Consulta somente leitura da frota (RN001/RN003).
 *
 * Contrato da HU 812: `busca` em texto livre, os filtros de coluna como listas
 * (`acronimo[]`, `embarcacao[]`, `tipo[]`, `situacao[]`), paginação 1-based em `page`/`pageSize`
 * e ordenação em `sort` no formato `campo:direção`. Os critérios são combinados
 * em conjunto (RN004) e o recorte por privilégio é aplicado pelo próprio serviço.
 */
export async function listEmbarcacoes(
  request: ListEmbarcacoesParams = {},
): Promise<EmbarcacoesPage> {
  const {
    busca,
    filters,
    page = 1,
    pageSize = DEFAULT_EMBARCACOES_PAGE_SIZE,
    sort = DEFAULT_EMBARCACOES_SORT,
    signal,
  } = request;

  const params: Record<string, HttpQueryParam> = {};

  const term = busca?.trim();
  if (term) params.busca = term;

  for (const column of EMBARCACAO_FILTER_COLUMNS) {
    const values = cleanValues(filters?.[column]);
    if (values.length > 0) params[column] = values;
  }

  params.page = page;
  params.pageSize = pageSize;
  params.sort = formatEmbarcacoesSort(sort);

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(EMBARCACOES_PATHS.LIST, config);

  const embarcacoes = extractItems(response.data, COLLECTION_KEYS)
    .map(toEmbarcacao)
    .filter((embarcacao): embarcacao is Embarcacao => embarcacao !== null);

  return { embarcacoes, total: extractTotal(response.data) ?? embarcacoes.length };
}

/** Comparação de texto no idioma da tela: sem distinguir acento nem caixa. */
const COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** `OpcaoFiltroDTO` do contrato: `valor` e `rotulo`. O rótulo cai no valor quando ausente. */
function toFilterOption(raw: unknown): EmbarcacaoFilterOption | null {
  if (!isRecord(raw)) {
    const value = typeof raw === "string" ? raw.trim() : "";
    return value ? { value, label: value } : null;
  }

  const value = readFirstText(raw, "valor", "value", "id", "codigo").trim();
  if (!value) return null;

  return {
    value,
    label: readFirstText(raw, "rotulo", "label", "descricao", "nome").trim() || value,
  };
}

/**
 * Opções de uma coluna filtrável, já ordenadas pelo rótulo e sem repetição — o
 * serviço é a única origem das opções, que não dependem da página em tela.
 */
export async function listEmbarcacaoFilterOptions(
  request: ListEmbarcacaoFilterOptionsParams,
): Promise<EmbarcacaoFilterOption[]> {
  const { column, signal } = request;

  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(
    buildEmbarcacaoFilterOptionsPath(column),
    config,
  );

  const seen = new Set<string>();

  return extractItems(response.data, COLLECTION_KEYS)
    .map(toFilterOption)
    .filter((option): option is EmbarcacaoFilterOption => {
      if (option === null || seen.has(option.value)) return false;

      seen.add(option.value);
      return true;
    })
    .sort((first, second) => COLLATOR.compare(first.label, second.label));
}

/** Envelopes de detalhe próprios do domínio, além de `data`/`item`/`content`. */
const BASE_TECNICA_DETAIL_KEYS = ["embarcacao", "baseTecnica"];
const DETAIL_KEYS = ["embarcacao", "baseTecnica", "classe", "comercial", "consumoOperacao"];

/**
 * Campo de uma seção do detalhamento: `null` quando a fonte não tem o dado.
 *
 * Um número do contrato (ano, idade, POB) vira o seu próprio texto — nenhum
 * cálculo ou derivação é aplicado (RN005) —, e texto em branco equivale a
 * campo sem preenchimento.
 */
function readValue(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];

    if (typeof value === "string" && value.trim() !== "") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }

  return null;
}

function toEmbarcacaoBaseTecnica(raw: unknown): EmbarcacaoBaseTecnica | null {
  if (!isRecord(raw)) return null;

  return {
    nome: readValue(raw, "nome", "embarcacao"),
    nomeAntigo: readValue(raw, "nomeAntigo"),
    nomeAbreviado: readValue(raw, "nomeAbreviado"),
    acronimo: readValue(raw, "acronimo"),
    tipo: readValue(raw, "tipo"),
    empresaOrigem: readValue(raw, "empresaOrigem"),
    mmsi: readValue(raw, "mmsi"),
    vesselSignalLetters: readValue(raw, "vesselSignalLetters"),
    imo: readValue(raw, "imo"),
    grupo: readValue(raw, "grupo"),
    cnpjFaturamento: readValue(raw, "cnpjFaturamento"),
    // O protótipo chama os dois campos de "Nome do Armador" e "CNPJ do Armador".
    armador: readValue(raw, "armador", "nomeArmador"),
    cnpj: readValue(raw, "cnpj", "cnpjArmador"),
    centroCusto: readValue(raw, "centroCusto"),
    sisterVessels: readValue(raw, "sisterVessels"),
    possuiReb: readValue(raw, "possuiReb"),
    bandeiraSuspensa: readValue(raw, "bandeiraSuspensa"),
    bandeira: readValue(raw, "bandeira"),
    paisConstrucao: readValue(raw, "paisConstrucao"),
    estaleiroConstrucao: readValue(raw, "estaleiroConstrucao"),
    anoBatimentoQuilha: readValue(raw, "anoBatimentoQuilha"),
    anoFinalConstrucao: readValue(raw, "anoFinalConstrucao"),
    idade: readValue(raw, "idade"),
    anoModernizacao: readValue(raw, "anoModernizacao"),
    sistemaGerencial: readValue(raw, "sistemaGerencial"),
    totalPessoas: readValue(raw, "totalPessoas"),
    situacaoEmbarcacao: readValue(raw, "situacaoEmbarcacao", "situacao"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Base Técnica da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1724: `GET /api/embarcacoes/{id}/bases-tecnicas`, somente
 * leitura e sem nenhuma escrita (RN003) — a consulta não gera registro de
 * auditoria. Devolve `null` quando a resposta chega sem o registro; a tela lê
 * esse caso como embarcação não encontrada.
 */
export async function getEmbarcacaoBaseTecnica({
  id,
  signal,
}: GetEmbarcacaoBaseTecnicaParams): Promise<EmbarcacaoBaseTecnica | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoBaseTecnicaPath(id), config);

  return toEmbarcacaoBaseTecnica(extractDetail(response.data, BASE_TECNICA_DETAIL_KEYS));
}

const DIMENSOES_DETAIL_KEYS = ["embarcacao", "dimensoes"];

/**
 * Campo dimensional: o número como armazenado na fonte, sem arredondar nem
 * converter unidade (RN005). Um número em texto é aceito porque o envelope do
 * BFF pode serializá-lo assim; qualquer outro conteúdo equivale a campo sem
 * preenchimento. Zero é valor legítimo e não vira `null`.
 */
function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];

  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function toEmbarcacaoDimensoes(raw: unknown): EmbarcacaoDimensoes | null {
  if (!isRecord(raw)) return null;

  return {
    comprimentoTotal: readNumber(raw, "comprimentoTotal"),
    comprimentoEntrePerpendiculares: readNumber(raw, "comprimentoEntrePerpendiculares"),
    caladoMax: readNumber(raw, "caladoMax"),
    caladoDocagem: readNumber(raw, "caladoDocagem"),
    bocaMoldada: readNumber(raw, "bocaMoldada"),
    arqueacaoBruta: readNumber(raw, "arqueacaoBruta"),
    arqueacaoLiquida: readNumber(raw, "arqueacaoLiquida"),
    tonelagemPorteBruto: readNumber(raw, "tonelagemPorteBruto"),
    atualizacaoDimensoes: readValue(raw, "atualizacaoDimensoes"),
    comprimentoConves: readNumber(raw, "comprimentoConves"),
    larguraConves: readNumber(raw, "larguraConves"),
    areaTotalConves: readNumber(raw, "areaTotalConves"),
    resistenciaConves: readNumber(raw, "resistenciaConves"),
    capacidadeConves: readNumber(raw, "capacidadeConves"),
    comprimentoConvesLivreUtil: readNumber(raw, "comprimentoConvesLivreUtil"),
    larguraConvesLivreUtil: readNumber(raw, "larguraConvesLivreUtil"),
    areaConvesLivreUtil: readNumber(raw, "areaConvesLivreUtil"),
    atualizacaoCargoDeck: readValue(raw, "atualizacaoCargoDeck"),
    deslocamentoCaladoDocagem: readNumber(raw, "deslocamentoCaladoDocagem"),
    caladoHidrostaticoDocagem: readNumber(raw, "caladoHidrostaticoDocagem"),
    tamanhoApendices: readNumber(raw, "tamanhoApendices"),
    equipamentoRetratil: readValue(raw, "equipamentoRetratil"),
    caladoRealDocagem: readNumber(raw, "caladoRealDocagem"),
    atualizacaoDocagem: readValue(raw, "atualizacaoDocagem"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Dimensões Principais da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1728: `GET /api/embarcacoes/{id}/dimensoes`, somente leitura e
 * sem nenhuma escrita (RN003) — a consulta não gera registro de auditoria.
 * Devolve `null` quando a resposta chega sem o registro; a tela lê esse caso
 * como embarcação não encontrada.
 */
export async function getEmbarcacaoDimensoes({
  id,
  signal,
}: GetEmbarcacaoDimensoesParams): Promise<EmbarcacaoDimensoes | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoDimensoesPath(id), config);

  return toEmbarcacaoDimensoes(extractDetail(response.data, DIMENSOES_DETAIL_KEYS));
}

const TANQUES_DETAIL_KEYS = ["embarcacao", "tanques"];

function toEmbarcacaoTanques(raw: unknown): EmbarcacaoTanques | null {
  if (!isRecord(raw)) return null;

  return {
    capacidadeTotalCombustivel: readNumber(raw, "capacidadeTotalCombustivel"),
    capacidadeTotalAgua: readNumber(raw, "capacidadeTotalAgua"),
    capacidadeTotalOro: readNumber(raw, "capacidadeTotalOro"),
    capacidadeTotalAguaLastro: readNumber(raw, "capacidadeTotalAguaLastro"),
    capacidadeTotalOleoBase: readNumber(raw, "capacidadeTotalOleoBase"),
    capacidadeTotalSalmoura: readNumber(raw, "capacidadeTotalSalmoura"),
    capacidadeTotalLama: readNumber(raw, "capacidadeTotalLama"),
    capacidadeTotalGranel: readNumber(raw, "capacidadeTotalGranel"),
    comentarios: readValue(raw, "comentarios"),
    atualizacaoCapacidade: readValue(raw, "atualizacaoCapacidade"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

const VAZAO_BOMBAS_DETAIL_KEYS = ["embarcacao", "vazaoBombas"];

function toEmbarcacaoVazaoBombas(raw: unknown): EmbarcacaoVazaoBombas | null {
  if (!isRecord(raw)) return null;

  return {
    vazaoCombustivel: readNumber(raw, "vazaoCombustivel"),
    pressaoCombustivel: readNumber(raw, "pressaoCombustivel"),
    vazaoAgua: readNumber(raw, "vazaoAgua"),
    pressaoAgua: readNumber(raw, "pressaoAgua"),
    vazaoOro: readNumber(raw, "vazaoOro"),
    pressaoOro: readNumber(raw, "pressaoOro"),
    vazaoAguaLastro: readNumber(raw, "vazaoAguaLastro"),
    pressaoAguaLastro: readNumber(raw, "pressaoAguaLastro"),
    vazaoOleoBase: readNumber(raw, "vazaoOleoBase"),
    pressaoOleoBase: readNumber(raw, "pressaoOleoBase"),
    vazaoSalmoura: readNumber(raw, "vazaoSalmoura"),
    pressaoSalmoura: readNumber(raw, "pressaoSalmoura"),
    vazaoLama: readNumber(raw, "vazaoLama"),
    pressaoLama: readNumber(raw, "pressaoLama"),
    vazaoGranel: readNumber(raw, "vazaoGranel"),
    pressaoGranel: readNumber(raw, "pressaoGranel"),
    atualizacaoVazaoBomba: readValue(raw, "atualizacaoVazaoBomba"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Tanques da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1732: `GET /api/embarcacoes/{id}/tanques`, somente leitura e
 * sem nenhuma escrita — a consulta não gera registro de auditoria. Devolve
 * `null` quando a resposta chega sem o registro; a tela lê esse caso como
 * embarcação não encontrada.
 */
export async function getEmbarcacaoTanques({
  id,
  signal,
}: GetEmbarcacaoTanquesParams): Promise<EmbarcacaoTanques | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoTanquesPath(id), config);

  return toEmbarcacaoTanques(extractDetail(response.data, TANQUES_DETAIL_KEYS));
}

/**
 * Seção Vazão das Bombas da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1735: `GET /api/embarcacoes/{id}/vazao-bombas`, somente
 * leitura e sem nenhuma escrita — a consulta não gera registro de auditoria.
 * Devolve `null` quando a resposta chega sem o registro; a tela lê esse caso
 * como embarcação não encontrada.
 */
export async function getEmbarcacaoVazaoBombas({
  id,
  signal,
}: GetEmbarcacaoVazaoBombasParams): Promise<EmbarcacaoVazaoBombas | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoVazaoBombasPath(id), config);

  return toEmbarcacaoVazaoBombas(extractDetail(response.data, VAZAO_BOMBAS_DETAIL_KEYS));
}

const SISTEMAS_DP_DETAIL_KEYS = ["embarcacao", "sistemasDp"];

function toEmbarcacaoSistemasDp(raw: unknown): EmbarcacaoSistemasDp | null {
  if (!isRecord(raw)) return null;

  return {
    dp: readValue(raw, "dp"),
    fabricanteModelo: readValue(raw, "fabricanteModelo"),
    sistemaReferenciaRelativo1: readValue(raw, "sistemaReferenciaRelativo1"),
    sistemaReferenciaRelativo2: readValue(raw, "sistemaReferenciaRelativo2"),
    atualizacaoDp: readValue(raw, "atualizacaoDp"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Sistemas de DP da embarcação acionada na lupa da listagem.
 *
 * Contrato da US-1799: `GET /api/embarcacoes/{id}/sistemas-dp`, somente leitura
 * e sem nenhuma escrita (RN003) — a consulta não gera registro de auditoria.
 * Devolve `null` quando a resposta chega sem o registro; a tela lê esse caso
 * como embarcação não encontrada.
 */
export async function getEmbarcacaoSistemasDp({
  id,
  signal,
}: GetEmbarcacaoSistemasDpParams): Promise<EmbarcacaoSistemasDp | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoSistemasDpPath(id), config);

  return toEmbarcacaoSistemasDp(extractDetail(response.data, SISTEMAS_DP_DETAIL_KEYS));
}

const ACOMODACOES_DETAIL_KEYS = ["embarcacao"];

function toEmbarcacaoAcomodacaoTipo(raw: unknown): EmbarcacaoAcomodacaoTipo | null {
  if (!isRecord(raw)) return null;

  return {
    tipo: readValue(raw, "tipo"),
    quantidade: readNumber(raw, "quantidade"),
    quantoComporta: readNumber(raw, "quantoComporta"),
    comentario: readValue(raw, "comentario"),
  };
}

function toEmbarcacaoAcomodacoes(raw: unknown): EmbarcacaoAcomodacoes | null {
  if (!isRecord(raw)) return null;

  const acomodacoes = extractItems(raw.acomodacoes)
    .map(toEmbarcacaoAcomodacaoTipo)
    .filter((tipo): tipo is EmbarcacaoAcomodacaoTipo => tipo !== null);

  return {
    possuiEnfermaria: readValue(raw, "possuiEnfermaria"),
    possuiAcademia: readValue(raw, "possuiAcademia"),
    atualizacaoAcomodacao: readValue(raw, "atualizacaoAcomodacao"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
    acomodacoes,
  };
}

/**
 * Seção Acomodações da embarcação acionada na lupa da listagem.
 *
 * Contrato da US-1797: `GET /api/embarcacoes/{id}/acomodacoes`, somente leitura
 * e sem nenhuma escrita (RN003) — a consulta não gera registro de auditoria.
 * Devolve `null` quando a resposta chega sem o registro; a tela lê esse caso
 * como embarcação não encontrada.
 */
export async function getEmbarcacaoAcomodacoes({
  id,
  signal,
}: GetEmbarcacaoAcomodacoesParams): Promise<EmbarcacaoAcomodacoes | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoAcomodacoesPath(id), config);

  return toEmbarcacaoAcomodacoes(extractDetail(response.data, ACOMODACOES_DETAIL_KEYS));
}

const MOTORES_COLLECTION_KEYS = ["embarcacao", "motores"];

function toEmbarcacaoMotor(raw: unknown): EmbarcacaoMotor | null {
  if (!isRecord(raw)) return null;

  return {
    motor: readValue(raw, "motor"),
    marca: readValue(raw, "marca"),
    modelo: readValue(raw, "modelo"),
    numeroSerie: readValue(raw, "numeroSerie"),
    codigoTmMaster: readValue(raw, "codigoTmMaster"),
    empresaAutomacaoResponsavel: readValue(raw, "empresaAutomacaoResponsavel"),
    potencia: readNumber(raw, "potencia"),
    rpm: readNumber(raw, "rpm"),
    tbo: readNumber(raw, "tbo"),
    periodicidade: readNumber(raw, "periodicidade"),
    ativo: readValue(raw, "ativo"),
    ordemMotor: readNumber(raw, "ordemMotor"),
    atualizacaoMotores: readValue(raw, "atualizacaoMotores"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Motores da embarcação acionada na lupa da listagem.
 *
 * Contrato da US-1800: `GET /api/embarcacoes/{id}/motores`, somente leitura e
 * sem nenhuma escrita (RN003) — a consulta não gera registro de auditoria.
 * Devolve a lista de motores como recebida; a embarcação sem motores volta como
 * lista vazia (estado vazio), distinta da embarcação inexistente (404).
 */
export async function getEmbarcacaoMotores({
  id,
  signal,
}: GetEmbarcacaoMotoresParams): Promise<EmbarcacaoMotor[]> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoMotoresPath(id), config);

  return extractItems(response.data, MOTORES_COLLECTION_KEYS)
    .map(toEmbarcacaoMotor)
    .filter((motor): motor is EmbarcacaoMotor => motor !== null);
}

function toEmbarcacaoClasse(raw: unknown): EmbarcacaoClasse | null {
  if (!isRecord(raw)) return null;

  return {
    notacaoClasse: readValue(raw, "notacaoClasse"),
    validadeNotacaoClasse: readValue(raw, "validadeNotacaoClasse"),
    classificadoraAtual: readValue(raw, "classificadoraAtual"),
    ultimoReportClasse: readValue(raw, "ultimoReportClasse"),
    anualFrom: readValue(raw, "anualFrom"),
    anualTo: readValue(raw, "anualTo"),
    especialFrom: readValue(raw, "especialFrom"),
    especialTo: readValue(raw, "especialTo"),
    ultimaEspecial: readValue(raw, "ultimaEspecial"),
    intermediariaFrom: readValue(raw, "intermediariaFrom"),
    intermediariaTo: readValue(raw, "intermediariaTo"),
    ultimaIntermediaria: readValue(raw, "ultimaIntermediaria"),
    ultimaBottom: readValue(raw, "ultimaBottom"),
    condicaoUltimaBottom: readValue(raw, "condicaoUltimaBottom"),
    bottomTo: readValue(raw, "bottomTo"),
    thrusterTo: readValue(raw, "thrusterTo"),
    propulsaoTo: readValue(raw, "propulsaoTo"),
    ultimoFmea: readValue(raw, "ultimoFmea"),
    vencimentoFmea: readValue(raw, "vencimentoFmea"),
    // `atualizacaoClasse` do contrato não integra os 19 campos da seção e
    // serve de alternativa apenas quando o cabeçalho não recebe a sua data.
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "atualizacaoClasse"),
  };
}

function toEmbarcacaoComercial(raw: unknown): EmbarcacaoComercial | null {
  if (!isRecord(raw)) return null;

  return {
    clienteAtual: readValue(raw, "clienteAtual"),
    observacoes: readValue(raw, "observacoes"),
    inicioContrato: readValue(raw, "inicioContrato"),
    fimContrato: readValue(raw, "fimContrato"),
    extensaoContrato: readValue(raw, "extensaoContrato"),
    // O contrato traz `atualizacaoComercial` além de `ultimaAtualizacao`, e o
    // protótipo só desenha uma data, junto ao título: a data específica da
    // seção vale como atualização dela quando a genérica não vem.
    ultimaAtualizacao: readValue(
      raw,
      "ultimaAtualizacao",
      "dataUltimaAtualizacao",
      "atualizacaoComercial",
    ),
  };
}

/**
 * Seção Classe da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1726: `GET /api/embarcacoes/{id}/classes`, somente leitura e
 * sem nenhuma escrita (RN003) — a consulta não gera registro de auditoria.
 * Devolve `null` quando a resposta chega sem o registro; a tela lê esse caso
 * como embarcação não encontrada.
 */
export async function getEmbarcacaoClasse({
  id,
  signal,
}: GetEmbarcacaoClasseParams): Promise<EmbarcacaoClasse | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoClassePath(id), config);

  return toEmbarcacaoClasse(extractDetail(response.data, DETAIL_KEYS));
}

/**
 * Seção Comercial da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1725: `GET /api/embarcacoes/{id}/comerciais`, somente leitura
 * e sem nenhuma escrita — a consulta não gera registro de auditoria. Devolve
 * `null` quando a resposta chega sem o registro; a tela lê esse caso como
 * embarcação não encontrada.
 */
export async function getEmbarcacaoComercial({
  id,
  signal,
}: GetEmbarcacaoComercialParams): Promise<EmbarcacaoComercial | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoComercialPath(id), config);

  return toEmbarcacaoComercial(extractDetail(response.data, DETAIL_KEYS));
}

function toEmbarcacaoConsumoOperacao(raw: unknown): EmbarcacaoConsumoOperacao | null {
  if (!isRecord(raw)) return null;

  return {
    consumoDp: readValue(raw, "consumoDp"),
    consumoFdAt: readValue(raw, "consumoFdAt"),
    consumoNvVelServico: readValue(raw, "consumoNvVelServico"),
    consumoNvVelTotal: readValue(raw, "consumoNvVelTotal"),
    consumoSm: readValue(raw, "consumoSm"),
    consumoNvVelEconomica: readValue(raw, "consumoNvVelEconomica"),
    atualizacaoConsumo: readValue(raw, "atualizacaoConsumo", "dataAtualizacaoConsumo"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Seção Consumo Modo Operação da embarcação acionada na lupa da listagem.
 *
 * Contrato da HU 1730: `GET /api/embarcacoes/{id}/consumo-operacao`, somente
 * leitura e sem registro de auditoria (RN004). Os consumos vêm como gravados na
 * fonte, sem recálculo. Devolve `null` quando a resposta chega sem o registro;
 * a tela lê esse caso como embarcação não encontrada.
 */
export async function getEmbarcacaoConsumoOperacao({
  id,
  signal,
}: GetEmbarcacaoConsumoOperacaoParams): Promise<EmbarcacaoConsumoOperacao | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(
    buildEmbarcacaoConsumoOperacaoPath(id),
    config,
  );

  return toEmbarcacaoConsumoOperacao(extractDetail(response.data, DETAIL_KEYS));
}
